import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  CreateSignatureRequest,
  GetSignatureRequestStatus,
  ListRequiredWaivers,
  RequiredWaiverTemplate,
  SubscribeSignatureRequestStatus,
} from '@/modules/auth/use-cases'
import { reportError } from '@/shared/infra/observability'
import { WAIVER_IFRAME_SANDBOX } from '@/shared/config'
import {
  loadWaiverProgress,
  saveWaiverProgress,
  waiverProgressKey,
  type WaiverProgress,
} from '../join-draft-store'

const POLL_INTERVAL_MS = 15000
const CALM_MESSAGE_DELAY_MS = 30000
const SIGNED_STATUSES = new Set(['signed', 'completed'])

function messageIndicatesCompletion(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const normalized = value.toLowerCase()
  return normalized.includes('signed') || normalized.includes('complet')
}

export function isWaiverCompletionHint(data: unknown): boolean {
  if (typeof data === 'string') return messageIndicatesCompletion(data)
  if (typeof data !== 'object' || data === null) return false
  const record = data as Record<string, unknown>
  return (
    messageIndicatesCompletion(record.type) ||
    messageIndicatesCompletion(record.status) ||
    messageIndicatesCompletion(record.action) ||
    messageIndicatesCompletion(record.event)
  )
}

export function isDocumentCompletedMessage(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false
  return (data as Record<string, unknown>).action === 'document-completed'
}

export type WaiverSignerGuestData = {
  firstName: string
  lastName: string
  email: string
  dateOfBirth?: string
}

export type UseWaiverSignerParams = {
  enabled: boolean
  orgId: string
  orgType: 'club' | 'outfitter'
  token: string
  guestData: WaiverSignerGuestData
  isMinor: boolean
  age?: number
  onListRequiredWaivers: ListRequiredWaivers
  onCreateSignatureRequest: CreateSignatureRequest
  onGetSignatureRequestStatus: GetSignatureRequestStatus
  onSubscribeSignatureRequestStatus: SubscribeSignatureRequestStatus
}

export type WaiverSignerState = {
  isLoadingRequired: boolean
  requiredCount: number
  signedCount: number
  currentTemplateName?: string
  currentEmbedUrl?: string
  isSigning: boolean
  isStartingSignature: boolean
  isAwaitingCompletion: boolean
  isPolling: boolean
  isWaitingLong: boolean
  isCheckingAgain: boolean
  isCheckAgainPending: boolean
  allSigned: boolean
  error?: string
  sandbox: string
  isMinor: boolean
  age?: number
  signatureRequestIds: string[]
  startSigning: () => void
  reopen: () => void
  dismiss: () => void
  checkAgain: () => void
}

type SignerStatus =
  | 'idle'
  | 'loadingRequired'
  | 'ready'
  | 'signing'
  | 'allSigned'
  | 'error'

export function useWaiverSigner(params: UseWaiverSignerParams): WaiverSignerState {
  const {
    enabled,
    orgId,
    orgType,
    token,
    guestData,
    isMinor,
    age,
    onListRequiredWaivers,
    onCreateSignatureRequest,
    onGetSignatureRequestStatus,
    onSubscribeSignatureRequestStatus,
  } = params

  const progressKey = waiverProgressKey(token)
  const restoredProgressRef = useRef<WaiverProgress | null | undefined>(undefined)
  if (restoredProgressRef.current === undefined) {
    restoredProgressRef.current = loadWaiverProgress(progressKey)
  }
  const restoredProgress = restoredProgressRef.current

  const [status, setStatus] = useState<SignerStatus>('idle')
  const [unsigned, setUnsigned] = useState<RequiredWaiverTemplate[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(
    () => restoredProgress?.collectedIds?.length ?? 0,
  )
  const [collectedIds, setCollectedIds] = useState<string[]>(
    () => restoredProgress?.collectedIds ?? [],
  )
  const [currentSignatureRequestId, setCurrentSignatureRequestId] = useState<
    string | undefined
  >(undefined)
  const [currentEmbedUrl, setCurrentEmbedUrl] = useState<string | undefined>(undefined)
  const [currentTemplateName, setCurrentTemplateName] = useState<string | undefined>(undefined)
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [isWaitingLong, setIsWaitingLong] = useState<boolean>(false)
  const [isCheckingAgain, setIsCheckingAgain] = useState<boolean>(false)
  const [isCheckAgainPending, setIsCheckAgainPending] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isStartingSignature, setIsStartingSignature] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const listedRef = useRef<boolean>(false)
  const creatingRef = useRef<boolean>(false)
  const activeRequestIdRef = useRef<string | null>(null)
  const expectedOriginRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const calmMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollSeqRef = useRef<number>(0)
  const identityRef = useRef<string | null>(null)

  const identityKey = useMemo(() => token, [token])

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    if (calmMessageTimerRef.current) {
      clearTimeout(calmMessageTimerRef.current)
      calmMessageTimerRef.current = null
    }
  }, [])

  const collectAndAdvance = useCallback(
    (signatureRequestId: string) => {
      setCollectedIds((prev) =>
        prev.includes(signatureRequestId) ? prev : [...prev, signatureRequestId],
      )
      activeRequestIdRef.current = null
      expectedOriginRef.current = null
      setCurrentEmbedUrl(undefined)
      setCurrentSignatureRequestId(undefined)
      setIsModalOpen(false)
      setIsPolling(false)
      setIsWaitingLong(false)
      setIsCheckAgainPending(false)
      clearPollTimer()
      setCurrentIndex((prev) => prev + 1)
    },
    [clearPollTimer],
  )

  const checkStatusNow = useCallback(
    async (signatureRequestId: string, seq: number): Promise<boolean> => {
      if (pollSeqRef.current !== seq) return false
      if (activeRequestIdRef.current !== signatureRequestId) return false
      try {
        const result = await onGetSignatureRequestStatus({ id: signatureRequestId })
        if (pollSeqRef.current !== seq) return false
        if (activeRequestIdRef.current !== signatureRequestId) return false
        if (SIGNED_STATUSES.has(result.status)) {
          collectAndAdvance(signatureRequestId)
          return true
        }
      } catch (err) {
        reportError(err, { fallbackMessage: 'Failed to check waiver status.' })
      }
      return false
    },
    [onGetSignatureRequestStatus, collectAndAdvance],
  )

  const beginPoll = useCallback(
    (signatureRequestId: string) => {
      clearPollTimer()
      const seq = pollSeqRef.current + 1
      pollSeqRef.current = seq
      setIsPolling(true)
      setIsWaitingLong(false)
      setIsCheckAgainPending(false)

      calmMessageTimerRef.current = setTimeout(() => {
        if (pollSeqRef.current === seq) setIsWaitingLong(true)
      }, CALM_MESSAGE_DELAY_MS)

      const scheduleNext = () => {
        pollTimerRef.current = setTimeout(() => {
          void tick()
        }, POLL_INTERVAL_MS)
      }

      const tick = async () => {
        if (pollSeqRef.current !== seq) return
        const resolved = await checkStatusNow(signatureRequestId, seq)
        if (resolved) return
        if (pollSeqRef.current !== seq) return
        scheduleNext()
      }

      scheduleNext()
    },
    [clearPollTimer, checkStatusNow],
  )

  const forceCheck = useCallback(() => {
    const signatureRequestId = activeRequestIdRef.current
    if (!signatureRequestId) return
    void checkStatusNow(signatureRequestId, pollSeqRef.current)
  }, [checkStatusNow])

  const openTemplate = useCallback(
    async (template: RequiredWaiverTemplate) => {
      if (creatingRef.current) return
      creatingRef.current = true
      setIsStartingSignature(true)
      setError(undefined)
      setCurrentTemplateName(template.name)
      try {
        const result = await onCreateSignatureRequest({
          orgId,
          orgType,
          templateId: template.templateId,
          guestData,
          isMinor,
          minorAge: isMinor ? age : undefined,
          inviteToken: token,
        })
        if (result.alreadySigned === true && result.signatureRequestId) {
          collectAndAdvance(result.signatureRequestId)
          return
        }
        if (!result.signatureRequestId || !result.embeddedSignUrl) {
          setStatus('error')
          setError('Unable to start the waiver signing session. Please try again.')
          return
        }
        let parsedOrigin: string
        try {
          parsedOrigin = new URL(result.embeddedSignUrl).origin
        } catch {
          setStatus('error')
          setError('Received an invalid signing URL. Please try again.')
          return
        }
        activeRequestIdRef.current = result.signatureRequestId
        expectedOriginRef.current = parsedOrigin
        setCurrentSignatureRequestId(result.signatureRequestId)
        setCurrentEmbedUrl(result.embeddedSignUrl)
        setIsModalOpen(true)
        beginPoll(result.signatureRequestId)
      } catch (err) {
        const message = reportError(err, {
          fallbackMessage: 'Unable to start the waiver signing session. Please try again.',
        })
        setStatus('error')
        setError(message)
      } finally {
        creatingRef.current = false
        setIsStartingSignature(false)
      }
    },
    [
      orgId,
      orgType,
      token,
      guestData,
      isMinor,
      age,
      onCreateSignatureRequest,
      collectAndAdvance,
      beginPoll,
    ],
  )

  useEffect(() => {
    if (identityRef.current !== null && identityRef.current !== identityKey) {
      listedRef.current = false
      setStatus('idle')
      setUnsigned([])
      setCurrentIndex(0)
      setCollectedIds([])
      setCurrentEmbedUrl(undefined)
      setCurrentTemplateName(undefined)
      setCurrentSignatureRequestId(undefined)
      setIsPolling(false)
      setIsWaitingLong(false)
      setIsCheckingAgain(false)
      setIsCheckAgainPending(false)
      setIsModalOpen(false)
      setIsStartingSignature(false)
      setWantsToSign(false)
      setError(undefined)
      activeRequestIdRef.current = null
      expectedOriginRef.current = null
      creatingRef.current = false
      pollSeqRef.current += 1
      clearPollTimer()
    }
    identityRef.current = identityKey
  }, [identityKey, clearPollTimer])

  useEffect(() => {
    if (!enabled) return
    if (listedRef.current) return
    listedRef.current = true
    setStatus('loadingRequired')
    onListRequiredWaivers({ orgId, orgType, token })
      .then((result) => {
        const required = result.waivers.filter((w) => w.required !== false)
        setUnsigned(required)
        setStatus('ready')
      })
      .catch((err) => {
        const message = reportError(err, {
          fallbackMessage: 'Failed to load required waivers.',
        })
        setStatus('error')
        setError(message)
      })
  }, [enabled, orgId, orgType, token, onListRequiredWaivers])

  useEffect(() => {
    if (status !== 'signing') return
    if (unsigned.length === 0) return
    if (currentIndex >= unsigned.length) {
      setStatus('allSigned')
      return
    }
    if (activeRequestIdRef.current) return
    if (currentEmbedUrl) return
    void openTemplate(unsigned[currentIndex])
  }, [status, unsigned, currentIndex, currentEmbedUrl, openTemplate])

  useEffect(() => {
    if (!enabled) return
    function handleMessage(event: MessageEvent) {
      const expected = expectedOriginRef.current
      const activeId = activeRequestIdRef.current
      if (!expected || !activeId) return
      if (event.origin !== expected) return
      if (isDocumentCompletedMessage(event.data)) setIsModalOpen(false)
      if (!isWaiverCompletionHint(event.data)) return
      forceCheck()
    }
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [enabled, forceCheck])

  useEffect(() => {
    if (!enabled) return
    if (!currentSignatureRequestId) return
    const unsubscribe = onSubscribeSignatureRequestStatus(currentSignatureRequestId, () => {
      forceCheck()
    })
    return unsubscribe
  }, [enabled, currentSignatureRequestId, onSubscribeSignatureRequestStatus, forceCheck])

  useEffect(() => {
    if (!enabled) return
    if (typeof document === 'undefined') return
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') forceCheck()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, forceCheck])

  useEffect(() => {
    return () => {
      pollSeqRef.current += 1
      clearPollTimer()
    }
  }, [clearPollTimer])

  const resumeCheckedRef = useRef<boolean>(false)
  useEffect(() => {
    if (!enabled) return
    if (resumeCheckedRef.current) return
    const activeId = restoredProgress?.activeSignatureRequestId
    if (!activeId || collectedIds.includes(activeId)) {
      resumeCheckedRef.current = true
      return
    }
    resumeCheckedRef.current = true
    onGetSignatureRequestStatus({ id: activeId })
      .then((result) => {
        if (SIGNED_STATUSES.has(result.status)) {
          collectAndAdvance(activeId)
        }
      })
      .catch((err) => {
        reportError(err, { fallbackMessage: 'Failed to recover your waiver status.' })
      })
  }, [
    enabled,
    restoredProgress,
    collectedIds,
    onGetSignatureRequestStatus,
    collectAndAdvance,
  ])

  useEffect(() => {
    if (!enabled) return
    saveWaiverProgress(progressKey, {
      collectedIds,
      activeSignatureRequestId: currentSignatureRequestId,
    })
  }, [enabled, progressKey, collectedIds, currentSignatureRequestId])

  const [wantsToSign, setWantsToSign] = useState<boolean>(false)

  const startSigning = useCallback(() => {
    setWantsToSign(true)
  }, [])

  useEffect(() => {
    if (!wantsToSign) return
    if ((status === 'ready' || status === 'error') && unsigned.length > 0) {
      setError(undefined)
      setStatus('signing')
      setWantsToSign(false)
    }
  }, [wantsToSign, status, unsigned.length])

  const dismiss = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const reopen = useCallback(() => {
    setIsCheckAgainPending(false)
    if (currentEmbedUrl && activeRequestIdRef.current) {
      setIsModalOpen(true)
      return
    }
    const template = unsigned[currentIndex]
    if (!template) return
    activeRequestIdRef.current = null
    expectedOriginRef.current = null
    setCurrentEmbedUrl(undefined)
    setIsPolling(false)
    setError(undefined)
    clearPollTimer()
    setStatus('signing')
    void openTemplate(template)
  }, [currentEmbedUrl, unsigned, currentIndex, clearPollTimer, openTemplate])

  const checkAgainInFlightRef = useRef<boolean>(false)
  const checkAgain = useCallback(() => {
    const signatureRequestId = activeRequestIdRef.current
    if (!signatureRequestId) return
    if (checkAgainInFlightRef.current) return
    checkAgainInFlightRef.current = true
    setIsCheckingAgain(true)
    setIsCheckAgainPending(false)
    void checkStatusNow(signatureRequestId, pollSeqRef.current).then((resolved) => {
      checkAgainInFlightRef.current = false
      setIsCheckingAgain(false)
      if (!resolved && activeRequestIdRef.current === signatureRequestId) {
        setIsCheckAgainPending(true)
      }
    })
  }, [checkStatusNow])

  const requiredCount = unsigned.length
  const signedCount = Math.min(currentIndex, requiredCount)
  const hasResolvedRequired =
    status === 'ready' || status === 'signing' || status === 'allSigned'
  const allSigned = enabled && hasResolvedRequired && currentIndex >= requiredCount

  return {
    isLoadingRequired: status === 'loadingRequired',
    requiredCount,
    signedCount,
    currentTemplateName,
    currentEmbedUrl,
    isSigning: status === 'signing' && Boolean(currentEmbedUrl) && isModalOpen,
    isStartingSignature: isStartingSignature || wantsToSign,
    isAwaitingCompletion:
      status === 'signing' && Boolean(currentEmbedUrl) && !isModalOpen,
    isPolling,
    isWaitingLong,
    isCheckingAgain,
    isCheckAgainPending,
    allSigned,
    error,
    sandbox: WAIVER_IFRAME_SANDBOX,
    isMinor,
    age,
    signatureRequestIds: collectedIds,
    startSigning,
    reopen,
    dismiss,
    checkAgain,
  }
}
